import { Button, Grid, IconButton, Stack, Typography} from "@mui/material";
import React, { useMemo } from "react";
import { Column } from "react-table";
import { useState } from "react";
import { apiStore } from "../../../api/ApiFactory";
import { useNavigate } from "react-router-dom";
import { formatDateToMMDDYYYY } from "../../../helpers/utils/formatDate";
import MainModal from "../../../components/modal/MainModal";
import ConfirmationBox from "../../../components/modal/ConfirmationBox";
import { useAuth } from "../../../store/hooks/useAuth";
import { GetAuthStatus } from "../../../services/AuthService";
import { Permissions, Roles } from "../../../helpers/enums/AuthConstants";
import EditIcon from '@mui/icons-material/Edit';
import ViewIcon from '@mui/icons-material/Visibility';
import { IPreAuth } from "../../../models/WarrantyPreAuth";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNotification } from "../../../store/context/NotificationContext";
import { Table } from "../../../components/table/Table";
import { useSelector } from "react-redux";
import { getClaimStatusFilterOptions } from "../../../store/slices/WarrantyClaim/WarrantyClaimSelector";
import { getAllDealerLocations } from "../../../store/slices/Dealer/DealerSelector";
import { ServerSelectColumnFilter } from "../../../components/table/filters/ServerSelectColumnFilter";
import { ClaimStatus } from "../../../helpers/enums/Warranty";

const PreAuthManagement = () => {
    const navigate = useNavigate();
    const [preAuthNumber, setPreAuthNumber] = useState<number>(-1); 
    const openDeletePreAuthModal = () => setDeletePreAuthModal(true);
    const closeDeletePreAuthModal = () => setDeletePreAuthModal(false);
    const [deletePreAuthModal, setDeletePreAuthModal] = useState(false);
    const { Toast } = useNotification();
    let authuser = useAuth().authuser;
    const [userRoleAndPermission, setUserRoleAndPermission] = useState({ role: authuser?.user.role, editWarrantyClaim: GetAuthStatus([Permissions.EditWarrantyClaim]).isAuthorized, viewWarrantyClaim: GetAuthStatus([Permissions.ViewWarrantyClaim]).isAuthorized });    
    const [disableControls, setDisableControls] = useState(false);    
    const claimStatusFilterOptions = useSelector(getClaimStatusFilterOptions);
    const dealerLocations = useSelector(getAllDealerLocations);

    //Calls a Callback function in Table.tsx to force a refresh while preserving state information.
    const [forceRefresh, setForceRefresh] = useState<any>();

    const handleDeletePreAuthBtnClick = (row: any, refreshHandler: any) => {
        setPreAuthNumber(row.preAuthNumber);
        setForceRefresh({refreshHandler}); //This is necessary to update the list page and maintain existing filters/sort/page.
        openDeletePreAuthModal();
    };

    const handlePreAuthCreate = () => {
        navigate(`/warranty/preauth/create`);
    }

    const handlePreAuthEdit = (row: any) => {
        navigate(`/warranty/preauth/edit/${row.preAuthNumber}`);
    }

    const handlePreAuthView = (row: any) => {
        navigate(`/warranty/preauth/view/${row.preAuthNumber}`);
    }

    const deletePreAuthOnConfirm = async (formValues: any) => {
        try {
            setDisableControls(true);
            closeDeletePreAuthModal();
            
            await apiStore.warrantypreauthApi.deletePreAuth(preAuthNumber)

            if(forceRefresh !== undefined) {
                forceRefresh.refreshHandler(); 
            }

            Toast.success({ title: `Pre-authorization deleted successfully.` });
            
        } catch (error) {
            if (error instanceof Error) {
                Toast.error({ title: error.message });
            } else {
                Toast.error({ title: 'An unknown error occurred during deletion.' });
            }
        } finally {
            setDisableControls(false);
        }
    };

    const getColumnDefintion =  () => {
        let columns: Column<IPreAuth>[] = [
            {
                Header: "Actions",
                accessor: "action",
                disableFilters: true,
                Cell: ({ row, forceRefreshHandler }: any) => {
                    const { claimStatus } = row.original;
                    return (
                        <>
                            {userRoleAndPermission.viewWarrantyClaim && !userRoleAndPermission.editWarrantyClaim && (
                                <>
                                    <IconButton title='View' onClick={() => handlePreAuthView(row.original)}>
                                        <ViewIcon style={{ marginRight: 2 }} />
                                    </IconButton>
                                </>
                            )}
                            {userRoleAndPermission.editWarrantyClaim && userRoleAndPermission.viewWarrantyClaim && (
                                <>

                                    {claimStatus === ClaimStatus.INITIATED && (
                                        <>
                                            <IconButton title='Edit' onClick={() => handlePreAuthEdit(row.original)}>
                                                <EditIcon style={{ marginRight: 2 }} />
                                            </IconButton>

                                            <IconButton title='Delete' onClick={() => handleDeletePreAuthBtnClick(row.original, forceRefreshHandler)}>
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </>
                                    )}

                                    {claimStatus === ClaimStatus.ACCEPTED && (
                                        <IconButton
                                            title={userRoleAndPermission.role === 'Admin' ? 'Edit' : 'View'}
                                            onClick={() =>
                                                userRoleAndPermission.role === 'Admin'
                                                    ? handlePreAuthEdit(row.original)
                                                    : handlePreAuthView(row.original)
                                            }
                                        >
                                            {userRoleAndPermission.role === 'Admin' ? (
                                                <EditIcon style={{ marginRight: 2 }} />
                                            ) : (
                                                <ViewIcon style={{ marginRight: 2 }} />
                                            )}
                                        </IconButton>
                                    )}

                                    {claimStatus !== ClaimStatus.INITIATED && claimStatus !== ClaimStatus.ACCEPTED && (
                                        <IconButton title='View' onClick={() => handlePreAuthView(row.original)}>
                                            <ViewIcon style={{ marginRight: 2 }} />
                                        </IconButton>
                                    )}
                                </>
                            )}
                        </>
                    );
                },
            },
            {
                Header: "Pre-Auth Number",
                accessor: "preAuthNumber",
                width: 200,
            },
            {
                Header: "HIN",
                accessor: "hin",
            },
            {
                Header: "Model",
                accessor: "model",
            },
            {
                Header: "Year",
                accessor: "modelYear",
            },            
            {
                Header: "Entry Date",
                accessor: "submitDate",
                width: 150,
                Cell: (cell: any) => (cell.value ? formatDateToMMDDYYYY(cell.value) : ""),
            },
            {
                Header: "Date of Failure",
                accessor: "failureDate",
                width: 150,
                Cell: (cell: any) => (cell.value ? formatDateToMMDDYYYY(cell.value) : ""),
            },
            {
                Header: "Status",
                accessor: "claimStatusDescription",
                Filter: ServerSelectColumnFilter,
                filterOptions: claimStatusFilterOptions
            }
        ];

        if(userRoleAndPermission.role !== Roles.DEALER) {
            columns.push({
                Header: "Dealer",
                accessor: "customerName",
                Filter:ServerSelectColumnFilter,
                filterOptions: dealerLocations.map((item) => {return {name: item.name, value: item.customerID}})
            });
        }

        return columns;
    }

    //Table Columns Structure
    const columns = useMemo(
        () => getColumnDefintion(),
        [claimStatusFilterOptions]
    );

    //Prevent unnecessary calls to server to fetch table data.
    if(claimStatusFilterOptions === undefined) {
        return <></>
    }

    return (
        <>
            <Stack
                className="header-label-container"
                direction="row"
                justifyContent="space-between"
            >
                <Typography variant="h5">{"Pre-Authorizations"}</Typography>
                
                {userRoleAndPermission.editWarrantyClaim && (
                    <Button variant="contained"  color="primary" onClick={handlePreAuthCreate}>
                        Create New Pre-Auth
                    </Button>
                )}
            </Stack>
            <MainModal open={deletePreAuthModal} handleClose={closeDeletePreAuthModal}>
                <ConfirmationBox
                    confirmTitle="Proceed"
                    cancelTitle="Close"
                    cancelClick={closeDeletePreAuthModal}
                    confirmClick={() => deletePreAuthOnConfirm(preAuthNumber)}
                    disable={disableControls}
                    message={`Record will be permanently removed from the system. Are you sure you want to delete pre-authorization number ${preAuthNumber}?`}
                />
            </MainModal>

            <Grid mt={2}>
                <Table<IPreAuth>
                    className="show-action-column"
                    name="preauth-table"
                    columns={columns}
                    serverOnChange={true}
                    apiGet="/warrantyclaimpreauth/GetList"
                    inlineLockState={false}
                    initialFilters={[{id: "claimStatusDescription", value: "NC"}]}
                />
            </Grid>
        </>
    );
};
export default PreAuthManagement;
